import React, { useState, useEffect } from "react";
import { Card, Collapse, Typography, Tag, Table, Spin, Alert } from "antd";
import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { generateInvoiceLinesFromPricing } from "../../services/pricingCalculationService";
import { Booking } from "../../types/booking";

const { Text, Title } = Typography;

interface RuleSelectionInfoProps {
  booking: Booking;
  companyId?: number;
}

interface PricingRule {
  id: number;
  name: string;
  origin_port: number | null;
  destination_port: number | null;
  transport_type: number | null;
  shipment_type: number | null;
  container_size: number | null;
  incoterm: number | null;
}

interface RuleSelectionData {
  selectedRule: PricingRule | null;
  totalRulesFound: number;
  specificityScore: number;
  matchingCriteria: string[];
  rejectedRules: Array<{
    rule: PricingRule;
    reasonsRejected: string[];
  }>;
}

const RuleSelectionInfo: React.FC<RuleSelectionInfoProps> = ({
  booking,
  companyId,
}) => {
  const [loading, setLoading] = useState(true);
  const [ruleData, setRuleData] = useState<RuleSelectionData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRuleSelectionInfo = async () => {
      try {
        setLoading(true);
        const result = await generateInvoiceLinesFromPricing(
          booking,
          companyId
        );

        if (result.error) {
          setError(result.error as string);
        } else if (result.ruleSelectionInfo) {
          setRuleData(result.ruleSelectionInfo);
        }
      } catch (err) {
        setError("Failed to fetch rule selection information");
        console.error("Error fetching rule selection info:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRuleSelectionInfo();
  }, [booking, companyId]);

  if (loading) {
    return (
      <Card
        size="small"
        title={
          <>
            <InfoCircleOutlined /> Rule Selection Analysis
          </>
        }
      >
        <Spin size="small" /> Loading rule analysis...
      </Card>
    );
  }

  if (error) {
    return (
      <Alert
        type="warning"
        message="Rule Analysis Unavailable"
        description={error}
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (!ruleData) {
    return null;
  }

  const rejectedRulesColumns = [
    {
      title: "Rule Name",
      dataIndex: ["rule", "name"],
      key: "name",
      width: "40%",
      render: (text: string) => (
        <Text ellipsis={{ tooltip: text }}>{text}</Text>
      ),
    },
    {
      title: "Rejection Reasons",
      dataIndex: "reasonsRejected",
      key: "reasons",
      render: (reasons: string[]) => (
        <div>
          {reasons.map((reason, index) => (
            <Tag key={index} color="red" icon={<CloseCircleOutlined />}>
              {reason}
            </Tag>
          ))}
        </div>
      ),
    },
  ];

  return (
    <Card
      size="small"
      title={
        <>
          <InfoCircleOutlined /> Rule Selection Analysis
        </>
      }
      style={{ marginBottom: 16 }}
    >
      <Collapse
        ghost
        size="small"
        items={[
          {
            key: "rule-analysis",
            label: (
              <Text strong>
                Decision Details
                {ruleData.selectedRule && (
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    Rule Selected
                  </Tag>
                )}
              </Text>
            ),
            children: (
              <div style={{ padding: "0 0 16px 0" }}>
                {/* Selected Rule Information */}
                {ruleData.selectedRule ? (
                  <div style={{ marginBottom: 16 }}>
                    <Title level={5}>
                      <CheckCircleOutlined
                        style={{ color: "#52c41a", marginRight: 8 }}
                      />
                      Selected Rule: {ruleData.selectedRule.name}
                    </Title>

                    <div style={{ paddingLeft: 24 }}>
                      <Text strong>Specificity Score: </Text>
                      <Tag color="blue">
                        {ruleData.specificityScore}/6 criteria specified
                      </Tag>
                      <br style={{ marginBottom: 8 }} />

                      <Text strong>Matching Criteria: </Text>
                      <div style={{ marginTop: 4 }}>
                        {ruleData.matchingCriteria.map((criteria, index) => (
                          <Tag
                            key={index}
                            color="green"
                            icon={<CheckCircleOutlined />}
                          >
                            {criteria}
                          </Tag>
                        ))}
                      </div>

                      <div
                        style={{
                          marginTop: 12,
                          padding: 8,
                          backgroundColor: "#f6ffed",
                          border: "1px solid #b7eb8f",
                          borderRadius: 4,
                        }}
                      >
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          💡 <strong>Why this rule was selected:</strong> This
                          rule had the highest specificity score (
                          {ruleData.specificityScore}) among{" "}
                          {ruleData.totalRulesFound} total rules found. More
                          specific rules (matching more criteria) automatically
                          take priority over general rules.
                        </Text>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Alert
                    type="warning"
                    message="No Rule Selected"
                    description="No pricing rules matched the booking criteria. Invoice lines must be added manually."
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                {/* Summary */}
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Analysis Summary:</Text>
                  <ul style={{ marginTop: 8, marginBottom: 0 }}>
                    <li>
                      Total rules evaluated:{" "}
                      <strong>{ruleData.totalRulesFound}</strong>
                    </li>
                    <li>
                      Rules rejected:{" "}
                      <strong>{ruleData.rejectedRules.length}</strong>
                    </li>
                    <li>
                      Rules matched:{" "}
                      <strong>{ruleData.selectedRule ? 1 : 0}</strong>
                    </li>
                  </ul>
                </div>

                {/* Rejected Rules */}
                {ruleData.rejectedRules.length > 0 && (
                  <div>
                    <Title level={5}>
                      <CloseCircleOutlined
                        style={{ color: "#ff4d4f", marginRight: 8 }}
                      />
                      Rejected Rules ({ruleData.rejectedRules.length})
                    </Title>

                    <Table
                      dataSource={ruleData.rejectedRules}
                      columns={rejectedRulesColumns}
                      rowKey={(record) => record.rule.id}
                      size="small"
                      pagination={false}
                      style={{ marginTop: 8 }}
                      scroll={{ x: 400 }}
                    />
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default RuleSelectionInfo;
